import './FooterList.scss';
import { useId } from 'react';
import classNames from 'classnames';
import { Icon } from '@components/icon';
import { Link, LinkProps, Text } from '@components/text';

export interface FooterListItemProps extends Omit<LinkProps, 'children' | 'isFooter'> {
  label: string;
}

export interface FooterListProps {
  label?: string;
  href?: LinkProps['href'];
  items?: Array<FooterListItemProps>;
  className?: string;
  brand?: 'pplus' | 'pluto';
  linkComponent?: React.ElementType;
}

/**
 * FooterList component, column with a label and link elements
 * @param label - list heading text; also used as the accessible name of the link list
 * @param href - URL for the heading link. When both `href` and `label` are provided, the heading becomes a clickable (crawlable) link without heading semantics, so screen readers announce it only as a link. Omit it to keep the heading as a plain `<h2>`.
 * @param items - array of objects with link data
 * @param className - optional additional class name
 * @param brand - brand typography variant ('pplus' or 'pluto')
 * @param linkComponent - Custom component to render links instead of a native `<a>` (e.g. Next.js Link, React Router Link). Used for both the item links and the heading link.
 */
export const FooterList: React.FC<FooterListProps> = ({
  items,
  label,
  href,
  className,
  brand = 'pplus',
  linkComponent,
}) => {
  const headingId = useId();
  const HeadingLink = linkComponent || 'a';
  const isPluto = brand === 'pluto';
  const isHeadingLink = Boolean(href && label);

  return (
    <div className={classNames('vds-footer-list', className)}>
      {label && (
        <Text
          id={headingId}
          component={isHeadingLink ? 'div' : 'h2'}
          className="vds-footer-list__heading"
          variant={isPluto ? 'caption' : 'body-02'}
          color="secondary"
          weight="semibold"
          size={isPluto ? 'medium' : 'small'}>
          {isHeadingLink ? (
            <HeadingLink className="vds-footer-list__heading-link" href={href}>
              {label}
            </HeadingLink>
          ) : (
            label
          )}
        </Text>
      )}
      <ul className="vds-footer-list__items" aria-labelledby={label ? headingId : undefined}>
        {items?.map(item => (
          <li className="vds-footer-list__item" key={item.label}>
            <Link
              color="tertiary"
              isFooter
              weight={isPluto ? 'bold' : 'regular'}
              size="small"
              linkComponent={linkComponent}
              {...item}>
              {item.label}
              <Icon name="chevron-forward" size="small" />
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
};
